import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { admin, ApiError, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { date, EMAIL_LABEL } from "../lib/format";
import type { AgreementsStatus, ApiKey, Company, Domain, List, NotificationSettings, Store, User } from "../lib/types";
import { Alert, Badge, Button, Card, Field, Modal, PageHeader, Toggle } from "../components/ui";

export default function Settings() {
  return (
    <>
      <PageHeader title="Setări" />
      <div className="space-y-6">
        <StoreSettings />
        <CompanySettings />
        <Notifications />
        <Domains />
        <Staff />
        <ApiKeys />
        <Agreements />
      </div>
    </>
  );
}

function StoreSettings() {
  const qc = useQueryClient();
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store") });
  const [f, setF] = useState({ name: "", tagline: "", accent_color: "#0f766e", tax: "21", prices_include_tax: true });
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  useEffect(() => {
    const s = store.data;
    if (s) setF({ name: s.name, tagline: (s.settings.tagline as string) ?? "", accent_color: (s.settings.accent_color as string) ?? "#0f766e",
      tax: String(s.tax_rate_bps / 100), prices_include_tax: s.prices_include_tax });
  }, [store.data]);
  const save = useMutation({
    mutationFn: () => admin.patch<Store>("/store", {
      name: f.name, tax_rate_bps: Math.round(Number(f.tax.replace(",", ".")) * 100), prices_include_tax: f.prices_include_tax,
      settings: { ...(store.data?.settings ?? {}), tagline: f.tagline },
    }),
    onSuccess: (s) => { qc.setQueryData(["store"], s); qc.invalidateQueries({ queryKey: ["me"] }); setMsg({ ok: true, text: "Setările au fost salvate." }); },
    onError: (e) => setMsg({ ok: false, text: errorMessage(e) }),
  });
  return (
    <Card title="Magazin">
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Nume magazin"><input className="input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Aspect" hint="Culorile, fonturile, logo-ul și pagina principală.">
          <Link to="/aspect" className="input block text-brand hover:underline">Deschide editorul de aspect →</Link>
        </Field>
        <Field label="Slogan" className="sm:col-span-2"><input className="input" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} /></Field>
        <Field label="Cotă TVA (%)"><input className="input" inputMode="decimal" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} /></Field>
        <Field label="Prețuri">
          <select className="input" value={f.prices_include_tax ? "1" : "0"} onChange={(e) => setF({ ...f, prices_include_tax: e.target.value === "1" })}>
            <option value="1">Includ TVA</option>
            <option value="0">Fără TVA (se adaugă la checkout)</option>
          </select>
        </Field>
        <div className="flex items-center gap-3 sm:col-span-2">
          <Button disabled={save.isPending}>Salvează</Button>
          {msg && <span className={`text-sm ${msg.ok ? "text-emerald-600" : "text-red-600"}`}>{msg.text}</span>}
        </div>
      </form>
    </Card>
  );
}

// Datele firmei: apar în subsolul magazinului (identificarea comerciantului cerută de lege) și completează
// automat paginile legale ({{firma}}, {{cui}} ...).
const COMPANY_FIELDS: { key: keyof Company; label: string; placeholder: string; hint?: string; wide?: boolean }[] = [
  { key: "legal_name", label: "Denumirea firmei", placeholder: "Casa Verde SRL" },
  { key: "vat_number", label: "CUI", placeholder: "RO12345674", hint: "Cu sau fără RO; verificăm cifra de control." },
  { key: "reg_number", label: "Nr. Reg. Com.", placeholder: "J2025000123009", hint: "Formatul nou ONRC (14 caractere) sau cel clasic, J40/1234/2020." },
  { key: "phone", label: "Telefon", placeholder: "0722 000 000" },
  { key: "email", label: "Email pentru clienți", placeholder: "contact@magazinul-meu.ro", hint: "Apare în subsol și în paginile legale (retururi, GDPR)." },
  { key: "address", label: "Sediul social", placeholder: "Str. Exemplu nr. 1, București, sector 1", wide: true },
];

function CompanySettings() {
  const qc = useQueryClient();
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store") });
  const [f, setF] = useState<Required<Company>>({ legal_name: "", vat_number: "", reg_number: "", address: "", email: "", phone: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  useEffect(() => {
    const c = (store.data?.settings.company ?? {}) as Company;
    setF({ legal_name: c.legal_name ?? "", vat_number: c.vat_number ?? "", reg_number: c.reg_number ?? "", address: c.address ?? "", email: c.email ?? "", phone: c.phone ?? "" });
  }, [store.data]);
  const save = useMutation({
    mutationFn: () => admin.patch<Store>("/store", {
      settings: { ...(store.data?.settings ?? {}), company: Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim()])) },
    }),
    onSuccess: (s) => { qc.setQueryData(["store"], s); qc.invalidateQueries({ queryKey: ["pages"] }); setErrors({}); setMsg({ ok: true, text: "Datele firmei au fost salvate." }); },
    onError: (e) => {
      const fe = e instanceof ApiError ? e.problem.errors : undefined;
      setErrors(Object.fromEntries(Object.entries(fe ?? {}).map(([k, v]) => [k.replace("settings.company.", ""), v])));
      setMsg({ ok: false, text: fe ? "Verifică câmpurile marcate." : errorMessage(e) });
    },
  });
  const ERR: Record<string, string> = {
    vat_number: "CUI invalid (cifra de control nu se potrivește).",
    reg_number: "Număr invalid: formatul clasic J40/1234/2020 sau cel nou ONRC, de 14 caractere (ex: J2025000123009), cu cifra de control corectă.",
    email: "Adresă de email invalidă.",
  };
  return (
    <Card title="Date firmă">
      <p className="mb-4 text-sm text-slate-500">
        Apar în subsolul fiecărei pagini a magazinului (legea cere identificarea comerciantului) și completează automat
        paginile legale din <Link to="/pages" className="text-brand underline">Pagini</Link>.
      </p>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {COMPANY_FIELDS.map((c) => (
          <Field key={c.key} label={c.label} hint={c.hint} error={errors[c.key] && (ERR[c.key] ?? errors[c.key])} className={c.wide ? "sm:col-span-2" : ""}>
            <input className="input" type={c.key === "email" ? "email" : "text"} placeholder={c.placeholder} value={f[c.key]}
              onChange={(e) => setF({ ...f, [c.key]: e.target.value })} />
          </Field>
        ))}
        <div className="flex items-center gap-3 sm:col-span-2">
          <Button disabled={save.isPending}>Salvează</Button>
          {msg && <span className={`text-sm ${msg.ok ? "text-emerald-600" : "text-red-600"}`}>{msg.text}</span>}
        </div>
      </form>
    </Card>
  );
}

// Emailurile tranzacționale: Reply-To pentru clienți, destinatarul notificărilor și ce se trimite.
function Notifications() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store") });
  const [f, setF] = useState<Required<NotificationSettings>>({ contact_email: "", orders_email: "", disabled: [] });
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  useEffect(() => {
    const n = (store.data?.settings.notifications ?? {}) as NotificationSettings;
    setF({ contact_email: n.contact_email ?? "", orders_email: n.orders_email ?? "", disabled: n.disabled ?? [] });
  }, [store.data]);
  const save = useMutation({
    mutationFn: () => admin.patch<Store>("/store", {
      settings: { ...(store.data?.settings ?? {}), notifications: { contact_email: f.contact_email.trim(), orders_email: f.orders_email.trim(), disabled: f.disabled } },
    }),
    onSuccess: (s) => { qc.setQueryData(["store"], s); setMsg({ ok: true, text: "Setările de notificări au fost salvate." }); },
    onError: (e) => setMsg({ ok: false, text: errorMessage(e) }),
  });
  const toggle = (t: string, on: boolean) => setF({ ...f, disabled: on ? f.disabled.filter((d) => d !== t) : [...f.disabled, t] });
  return (
    <Card title="Notificări pe email">
      <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email de contact al magazinului" hint="Răspunsurile clienților la emailurile de comandă ajung aici (Reply-To).">
            <input className="input" type="email" placeholder="contact@magazinul-meu.ro" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} />
          </Field>
          <Field label="Notificări de comenzi noi" hint={`Implicit: ${me?.user?.email ?? "adresa proprietarului"}.`}>
            <input className="input" type="email" placeholder={me?.user?.email ?? ""} value={f.orders_email} onChange={(e) => setF({ ...f, orders_email: e.target.value })} />
          </Field>
        </div>
        <div>
          <div className="mb-2 text-xs font-semibold text-slate-600">Ce emailuri se trimit</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.entries(EMAIL_LABEL).map(([t, label]) => (
              <Toggle key={t} checked={!f.disabled.includes(t)} onChange={(on) => toggle(t, on)} label={label} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button disabled={save.isPending}>Salvează</Button>
          {msg && <span className={`text-sm ${msg.ok ? "text-emerald-600" : "text-red-600"}`}>{msg.text}</span>}
        </div>
      </form>
    </Card>
  );
}

function Domains() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["domains"], queryFn: () => admin.get<List<Domain>>("/domains") });
  const [host, setHost] = useState("");
  const [error, setError] = useState<string>();
  const add = useMutation({
    mutationFn: () => admin.post("/domains", { host: host.trim().toLowerCase() }),
    onSuccess: () => { setHost(""); setError(undefined); qc.invalidateQueries({ queryKey: ["domains"] }); },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (h: string) => admin.del(`/domains/${h}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
    onError: (e) => setError(errorMessage(e)),
  });
  const primary = useMutation({
    mutationFn: (h: string) => admin.post<List<Domain>>(`/domains/${h}/primary`),
    onSuccess: (d) => { setError(undefined); qc.setQueryData(["domains"], d); },
    onError: (e) => setError(errorMessage(e)),
  });
  return (
    <Card title="Domenii">
      <ul className="mb-4 divide-y divide-slate-100 text-sm">
        {list.data?.data.map((d) => (
          <li key={d.host} className="flex items-center justify-between py-2">
            <span className="font-mono">{d.host} {d.is_primary && <Badge tone="blue">principal</Badge>}</span>
            {!d.is_primary && (
              <span className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={primary.isPending}
                  onClick={() => confirm(`Setezi ${d.host} ca domeniu principal? Adresele canonice, sitemap-ul și linkurile din emailuri vor folosi acest domeniu.`) && primary.mutate(d.host)}>
                  Setează ca principal
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del.mutate(d.host)}>Elimină</Button>
              </span>
            )}
          </li>
        ))}
      </ul>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
        <input className="input max-w-sm" placeholder="www.magazinul-meu.ro" value={host} onChange={(e) => setHost(e.target.value)} />
        <Button variant="secondary" disabled={!host}>Adaugă domeniu</Button>
      </form>
      <p className="mt-2 text-xs text-slate-400">Îndreaptă un record CNAME al domeniului către platformă; certificatul SSL se emite automat.</p>
      <p className="mt-1 text-xs text-slate-400">
        Domeniul <b>principal</b> e cel indexat de Google (adrese canonice, sitemap.xml, robots.txt) și cel din linkurile din emailuri și facturi.
        Setează-l pe domeniul propriu după ce acesta funcționează (DNS + certificat). Toate domeniile servesc în continuare magazinul.
      </p>
      {error && <div className="mt-3"><Alert>{error}</Alert></div>}
    </Card>
  );
}

function Staff() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canManage = me?.user?.role === "owner" || me?.user?.role === "admin";
  const list = useQuery({ queryKey: ["staff"], queryFn: () => admin.get<List<User>>("/staff") });
  const [form, setForm] = useState<{ email: string; name: string; password: string; role: string } | null>(null);
  const [error, setError] = useState<string>();
  const create = useMutation({
    mutationFn: () => admin.post("/staff", form),
    onSuccess: () => { setForm(null); qc.invalidateQueries({ queryKey: ["staff"] }); },
    onError: (e) => setError(errorMessage(e)),
  });
  const disable = useMutation({ mutationFn: (id: string) => admin.del(`/staff/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["staff"] }) });
  return (
    <Card title="Echipă" actions={canManage && <Button size="sm" variant="secondary" onClick={() => { setError(undefined); setForm({ email: "", name: "", password: "", role: "staff" }); }}>+ Membru</Button>}>
      <ul className="divide-y divide-slate-100 text-sm">
        {list.data?.data.map((u) => (
          <li key={u.id} className="flex items-center justify-between py-2">
            <span><b>{u.name || u.email}</b> <span className="text-slate-400">{u.email}</span></span>
            <span className="flex items-center gap-2">
              <Badge tone={u.role === "owner" ? "violet" : "gray"}>{u.role}</Badge>
              {canManage && u.role !== "owner" && u.id !== me?.user?.id && (
                <Button size="sm" variant="ghost" onClick={() => confirm(`Dezactivezi ${u.email}?`) && disable.mutate(u.id)}>Dezactivează</Button>
              )}
            </span>
          </li>
        ))}
      </ul>
      <Modal open={!!form} onClose={() => setForm(null)} title="Membru nou">
        {form && (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <Field label="Email"><input className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Nume"><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Parolă inițială" hint="Minim 10 caractere"><input className="input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            <Field label="Rol">
              <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="staff">Staff (operare comenzi și produse)</option>
                <option value="admin">Admin (și setări, echipă, chei API)</option>
              </select>
            </Field>
            {error && <Alert>{error}</Alert>}
            <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setForm(null)}>Anulează</Button><Button disabled={create.isPending}>Adaugă</Button></div>
          </form>
        )}
      </Modal>
    </Card>
  );
}

function ApiKeys() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canManage = me?.user?.role === "owner" || me?.user?.role === "admin";
  const list = useQuery({ queryKey: ["api-keys"], queryFn: () => admin.get<List<ApiKey>>("/api-keys"), enabled: canManage });
  const [created, setCreated] = useState<ApiKey | null>(null);
  const create = useMutation({
    mutationFn: (name: string) => admin.post<ApiKey>("/api-keys", { name, role: "staff" }),
    onSuccess: (k) => { setCreated(k); qc.invalidateQueries({ queryKey: ["api-keys"] }); },
  });
  const revoke = useMutation({ mutationFn: (id: string) => admin.del(`/api-keys/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }) });
  if (!canManage) return null;
  return (
    <Card title="Chei API" actions={<Button size="sm" variant="secondary" onClick={() => { const n = prompt("Numele cheii (ex: ERP, integrare curier)"); if (n) create.mutate(n); }}>+ Cheie</Button>}>
      {created?.key && (
        <div className="mb-4"><Alert tone="amber">Copiază cheia acum, nu va mai fi afișată: <code className="select-all break-all font-mono font-bold">{created.key}</code></Alert></div>
      )}
      <ul className="divide-y divide-slate-100 text-sm">
        {list.data?.data.length === 0 && <li className="py-2 text-slate-400">Nicio cheie.</li>}
        {list.data?.data.map((k) => (
          <li key={k.id} className="flex items-center justify-between py-2">
            <span><b>{k.name}</b> <code className="text-slate-400">{k.prefix}…</code> <span className="text-xs text-slate-400">{date(k.created_at)}</span></span>
            {k.revoked_at ? <Badge>revocată</Badge> : <Button size="sm" variant="ghost" onClick={() => confirm("Revoci cheia?") && revoke.mutate(k.id)}>Revocă</Button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// Acordurile cu platforma: ce versiune a acceptat magazinul, când și cine.
function Agreements() {
  const q = useQuery({ queryKey: ["agreements"], queryFn: () => admin.get<AgreementsStatus>("/agreements") });
  return (
    <Card title="Acorduri cu platforma">
      <ul className="divide-y divide-slate-100 text-sm">
        {q.data?.data.map((d) => (
          <li key={d.document} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <a href={`/legal/${d.document}`} target="_blank" className="font-semibold hover:text-brand">{d.title} ↗</a>
            <span className="flex items-center gap-2 text-slate-500">
              {d.accepted_at ? <>acceptat {date(d.accepted_at)}{d.accepted_by && ` de ${d.accepted_by}`} · versiunea {d.accepted_version}</> : "neacceptat"}
              {d.current ? <Badge tone="green">la zi</Badge> : <Badge tone="amber">versiune nouă</Badge>}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
